import React, { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Activity, ArrowRight, BarChart3, Building2, CalendarCheck, Cloud, Code2, Compass,
  Database, EyeOff, FileSpreadsheet, Gauge, GraduationCap, IndianRupee, Layers,
  LayoutDashboard, Lock, LogOut, MessageSquare, Monitor, MonitorCog, Network,
  PieChart, Server, Settings2, ShieldCheck, Shuffle, Sparkles, Trash2, UploadCloud,
  Upload, UserPlus, Users, Wrench
} from 'lucide-react';
import { PATHWAY_COMPARISON, PRODUCTS, PRODUCT_INDEX } from '../../data/productsData';
import styles from './ProductPage.module.css';

const ICONS = {
  Activity, ArrowRight, BarChart3, Building2, CalendarCheck, Cloud, Code2, Compass,
  Database, EyeOff, FileSpreadsheet, Gauge, GraduationCap, IndianRupee, Layers,
  LayoutDashboard, Lock, LogOut, MessageSquare, Monitor, MonitorCog, Network,
  PieChart, Server, Settings2, ShieldCheck, Shuffle, Sparkles, Trash2, UploadCloud,
  Upload, UserPlus, Users, Wrench
};

/* Icons are presentation, so they live here keyed by the heading rather than
   in the content file. A heading with no entry simply gets the fallback. */
const CARD_ICONS = {
  // One Rupee ERP
  'Onboard data your way': 'Upload',
  'Real semantic modeling': 'Layers',
  'Reports built for you': 'LayoutDashboard',
  'Ask it in plain English': 'MessageSquare',
  'Benchmarks no ERP offers': 'BarChart3',
  'Your own visuals, natively': 'PieChart',
  // Desktop
  'Pre-built templates': 'FileSpreadsheet',
  'No infrastructure': 'Gauge',
  'Standard KPI set': 'Activity',
  'A path onward': 'ArrowRight',
  // Enterprise Managed
  'Custom analytics, no infrastructure': 'Server',
  'Embedded in your product': 'Code2',
  'Analytics across a school base': 'Building2',
  'Maintained for you': 'Wrench',
  // Enterprise Native
  'Zero external data movement': 'ShieldCheck',
  'A native interface': 'MonitorCog',
  'An always-on analytics layer': 'Activity',
  'Customisation without limit': 'Settings2',
  // BenchmarkIQ — the problem
  'No external reference': 'Compass',
  'The data exists, but siloed': 'Database',
  'AnalyticaX is positioned to build it': 'Network',
  // BenchmarkIQ — pillars
  Contribute: 'Upload',
  Anonymise: 'EyeOff',
  Benchmark: 'BarChart3',
  // BenchmarkIQ — KPI universe
  'Financial health': 'IndianRupee',
  'Academic pulse': 'GraduationCap',
  'Operational health': 'CalendarCheck',
  'Admissions and retention': 'UserPlus',
  // BenchmarkIQ — privacy
  'Minimum cohort rule': 'Users',
  'Contribution is not visibility': 'Lock',
  'Opt-out registry': 'LogOut',
  'Noise injection and k-anonymity': 'Shuffle',
  'Annual purge rights': 'Trash2'
};

const Icon = ({ name, size = 20 }) => {
  const Glyph = ICONS[name] || Sparkles;
  return <Glyph size={size} strokeWidth={1.9} aria-hidden="true" />;
};

const Block = ({ title, intro, children, id }) => (
  <section className={styles.block} id={id}>
    {title ? <h2 className={styles.sectionTitle}>{title}</h2> : null}
    {intro ? <p className={styles.sectionIntro}>{intro}</p> : null}
    {children}
  </section>
);

const CardGrid = ({ items }) => (
  <div className={styles.cards}>
    {items.map((item) => (
      <article key={item.title} className={styles.card}>
        <span className={styles.cardIcon}>
          <Icon name={CARD_ICONS[item.title]} />
        </span>
        <h3 className={styles.cardTitle}>{item.title}</h3>
        <p className={styles.cardBody}>{item.body}</p>
      </article>
    ))}
  </div>
);

/* Three nodes with the data moving between them. The pathway pages differ
   almost entirely in this one picture, so it is worth drawing. */
const Flow = ({ steps }) => (
  <ol className={styles.flow}>
    {steps.map((step, i) => (
      <li key={step.title} className={styles.flowStep}>
        <span className={styles.flowIcon}>
          <Icon name={step.icon} size={22} />
        </span>
        <h3 className={styles.cardTitle}>{step.title}</h3>
        <p className={styles.cardBody}>{step.body}</p>
        {i < steps.length - 1 ? (
          <span className={styles.flowArrow} aria-hidden="true">
            <ArrowRight size={18} strokeWidth={2.2} />
          </span>
        ) : null}
      </li>
    ))}
  </ol>
);

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    if (product) document.title = `${product.name} — Analytica X`;
    return () => {
      document.title = 'Analytica X';
    };
  }, [product]);

  if (!product) return <Navigate to="/" replace />;

  const goToEnquiry = () => navigate('/', { state: { scrollTo: 'get-in-touch' } });

  return (
    <div className={styles.page}>
      <header className={styles.hero} id="overview">
        <div className="container">
          <div className={styles.heroGrid}>
            <div>
              <span className={styles.eyebrow}>{product.eyebrow}</span>
              <h1 className={styles.name}>{product.name}</h1>
              <p className={styles.tagline}>{product.tagline}</p>
              <p className={styles.intro}>{product.intro}</p>
              <div className={styles.heroActions}>
                <button type="button" className={styles.primaryBtn} onClick={goToEnquiry}>
                  Talk to us
                  <ArrowRight size={15} strokeWidth={2.2} aria-hidden="true" />
                </button>
                <Link to="/" className={styles.ghostBtn}>Back to home</Link>
              </div>
            </div>

            <div className={styles.facts}>
              {product.facts.map((fact) => (
                <div key={fact.label} className={styles.fact}>
                  <span className={styles.factLabel}>{fact.label}</span>
                  <span className={styles.factValue}>{fact.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className={styles.shell}>
        <div className="container">
          <div className={styles.shellGrid}>
            {/* The suite as a standing list rather than a strip at the foot,
                so moving between products never needs a scroll to the bottom */}
            <aside className={styles.sidebar}>
              <span className={styles.sidebarTitle}>Products</span>
              <nav className={styles.sidebarNav} aria-label="Products">
                {PRODUCT_INDEX.map((item) => {
                  const on = item.slug === slug;
                  return (
                    <Link
                      key={item.slug}
                      to={`/products/${item.slug}`}
                      className={on ? `${styles.navItem} ${styles.navItemOn}` : styles.navItem}
                      aria-current={on ? 'page' : undefined}
                    >
                      <span className={styles.navName}>
                        <Icon name={item.icon} size={15} />
                        {item.name}
                      </span>
                      <span className={styles.navTagline}>{item.tagline}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>

            <main className={styles.content}>
              {product.problem ? (
                <Block title={product.problem.title}>
                  <CardGrid items={product.problem.items} />
                </Block>
              ) : null}

              <Block title={product.capabilities.title} id="capabilities">
                <CardGrid items={product.capabilities.items} />
              </Block>

              {product.kpis ? (
                <Block title={product.kpis.title}>
                  <CardGrid items={product.kpis.items} />
                </Block>
              ) : null}

              {product.flow ? (
                <Block title={product.flow.title} id="flow">
                  <Flow steps={product.flow.steps} />
                </Block>
              ) : null}

              {product.decision ? (
                <Block id="decision">
                  <div className={styles.decision}>
                    <div>
                      <span className={styles.label}>Choose this when you say</span>
                      <p className={styles.decisionQuote}>
                        &ldquo;{product.decision.quote}&rdquo;
                      </p>
                    </div>
                    <div className={styles.decisionAction}>
                      <h3 className={styles.decisionActionTitle}>{product.decision.action}</h3>
                      <p className={styles.decisionActionBody}>{product.decision.body}</p>
                    </div>
                  </div>
                </Block>
              ) : null}

              {product.steps ? (
                <Block title={product.steps.title}>
                  <div className={styles.steps}>
                    {product.steps.items.map((item, i) => (
                      <div key={item.title} className={styles.step}>
                        <span className={styles.stepNum}>{String(i + 1).padStart(2, '0')}</span>
                        <h3 className={styles.cardTitle}>{item.title}</h3>
                        <p className={styles.cardBody}>{item.body}</p>
                      </div>
                    ))}
                  </div>
                </Block>
              ) : null}

              {product.privacy ? (
                <Block title={product.privacy.title} intro={product.privacy.intro} id="privacy">
                  <ul className={styles.privacyList}>
                    {product.privacy.items.map((item) => (
                      <li key={item.title}>
                        <span className={styles.listIcon}>
                          <Icon name={CARD_ICONS[item.title]} size={17} />
                        </span>
                        <h3 className={styles.cardTitle}>{item.title}</h3>
                        <p className={styles.cardBody}>{item.body}</p>
                      </li>
                    ))}
                  </ul>
                  <p className={styles.privacyNote}>{product.privacy.note}</p>
                </Block>
              ) : null}

              {product.segmentation ? (
                <Block
                  title={product.segmentation.title}
                  intro={product.segmentation.intro}
                  id="segmentation"
                >
                  <div className={styles.segGrid}>
                    <table className={styles.segTable}>
                      <tbody>
                        {product.segmentation.rows.map((row) => (
                          <tr key={row.label}>
                            <th scope="row">{row.label}</th>
                            <td>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className={styles.example}>
                      <span className={styles.exampleLabel}>Example query</span>
                      <p className={styles.exampleQuote}>
                        &ldquo;{product.segmentation.example}&rdquo;
                      </p>
                    </div>
                  </div>
                </Block>
              ) : null}

              {product.showComparison ? (
                <Block
                  title="Comparing the three pathways"
                  intro={PATHWAY_COMPARISON.intro}
                  id="pathways"
                >
                  <div className={styles.tableWrap}>
                    <table className={styles.compare}>
                      <thead>
                        <tr>
                          <th scope="col" aria-label="Attribute" />
                          {PATHWAY_COMPARISON.columns.map((column, i) => (
                            <th key={column} scope="col">
                              {column}
                              <span className={styles.compareBadge}>
                                {PATHWAY_COMPARISON.badges[i]}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {PATHWAY_COMPARISON.rows.map((row) => (
                          <tr key={row.label}>
                            <th scope="row">{row.label}</th>
                            {row.values.map((value, i) => (
                              <td key={PATHWAY_COMPARISON.columns[i]}>{value}</td>
                            ))}
                          </tr>
                        ))}
                        <tr>
                          <th scope="row">Read more</th>
                          {PATHWAY_COMPARISON.paths.map((path, i) => (
                            <td key={path}>
                              <Link to={path} className={styles.compareLink}>
                                {PATHWAY_COMPARISON.columns[i]} &rarr;
                              </Link>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Block>
              ) : null}

              {product.audience ? (
                <Block>
                  <div className={styles.audience}>
                    <span className={styles.label}>Who it is for</span>
                    <p className={styles.audienceText}>{product.audience}</p>
                  </div>
                </Block>
              ) : null}
            </main>
          </div>
        </div>
      </div>

      <section className={styles.closing}>
        <div className="container">
          <h2 className={styles.closingTitle}>See it against your own data</h2>
          <p className={styles.closingText}>
            Tell us how your institution runs and we will show you {product.name} working on a
            dataset like yours.
          </p>
          <button type="button" className={styles.primaryBtn} onClick={goToEnquiry}>
            Request a demo
            <ArrowRight size={15} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
